import React, { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Add, Error } from "@mui/icons-material";
import ActionButton, { ConditionalOutlinedButton } from "../../components/commonComponents/Buttons/ActionButton";
import { DisabledMutedGreyButton } from "../../components/commonComponents/Buttons/DisabledButton";
import { HtmlTooltip, SpaceBetweenStack, StyledMainBox } from "../../components/commonComponents/StyledComponents/styled";
import AddNewCampaign from "./ModalContent/AddCampaign";
import AccessControl from "../../components/commonComponents/Hoc/AccessControl";
import { checkUserPermissions, pageAccessAllowed } from "../../api_calls/userManagement";
import {
  getBottomNavRetailerSelectionFromCookies,
  isLockedOutsideAllRetailers,
  resolveRetailerSelectionOptions,
} from "../../api_calls/retailerSelection";
import CampaignList from "./listings/CampaignList";
import CampaignListFilters from "./filters/CampaignListFilters";
import { resetCampaignSheetData } from "../../api_calls/campaignSheet";
import { clearCampaignDetails, resetBrandReportView } from "../../api_calls/utils";
import { useSelector } from "react-redux";
import WorkspaceSections from "./ModalContent/WorkspaceSections";

// Shown when Sections is disabled outside the All Retailers bottom-nav view
const SECTIONS_ALL_RETAILERS_TOOLTIP = 'Only in "All Retailers"';

const Campaigns = () => {
  const Campaigns = useSelector((state) => state.userSelections);
  const [newCampaignModal, setNewCampaignModal] = useState(false);
  const [newWorkspaceSectionModal, setNewWorkspaceSectionModal] = useState(false);

  // check edit permission for the campaigns
  const checkEditAccess = pageAccessAllowed(["campaign_list_can_edit"], []);
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;

  // Prefer Redux bottom-nav selection so the lock stays in sync; fall back to cookie
  const bottomNavRetailerSelection =
    typeof Campaigns.bottom_nav_selected_retailer !== "undefined"
      ? Campaigns.bottom_nav_selected_retailer
      : getBottomNavRetailerSelectionFromCookies();
  // Sections is workspace-level — only when All Retailers can actually be chosen.
  // One company (static name, no dropdown) stays unlocked.
  const isSectionsLocked = isLockedOutsideAllRetailers(
    bottomNavRetailerSelection,
    resolveRetailerSelectionOptions(Campaigns)
  );

  // Warn on Sections when any root retailer is missing an impressions section.
  // Uses normalized map: report_section_availability.impression_sections[id] = true/false
  const impressionSectionsMap = Campaigns.report_section_availability?.impression_sections;
  const showErrorForSectionButton =
    typeof impressionSectionsMap !== "undefined" &&
    impressionSectionsMap !== null &&
    Object.values(impressionSectionsMap).some((isAvailable) => isAvailable === false);

  React.useEffect(() => {
    resetBrandReportView();
    clearCampaignDetails();
    // clear leftovers of campaign/product modals that were left without closing (e.g. browser back),
    // otherwise "New Campaign" gets prefilled with the previously edited campaign.
    resetCampaignSheetData();
  }, []);

  return (
    <>
      {/* ========== main component body =========== */}
      <StyledMainBox component="main">
        {/* <Box height={30} /> */}
        <SpaceBetweenStack
          direction={"row"}
          spacing={2}
          width={"100%"}
          justifyContent={"space-between"}
          display={"flex"}
        >
          <h1>Contact-based Campaigns</h1>

          {/* -- section button to configure campaign analytics tab settings at workspace level -- */}
          {
            permissionScope.campaign_reports_settings_can_view === true && (
              <Box sx={{ position: "relative", display: "inline-block" }}>
                {isSectionsLocked === true ? (
                  // Greyed out with tooltip when a specific retailer is selected in the bottom nav.
                  // sx keeps the same vertical rhythm as the active outlined button it replaces.
                  <DisabledMutedGreyButton
                    onClick={(e) => {
                      e.preventDefault();
                    }}
                    ariaText={"addWorkSpaceSections"}
                    label={"Sections"}
                    toolTipContent={SECTIONS_ALL_RETAILERS_TOOLTIP}
                    sx={{ mt: 3, mb: 2 }}
                  />
                ) : (
                  <ConditionalOutlinedButton
                    onClick={(e) => {
                      e.preventDefault();
                      setNewWorkspaceSectionModal(true);
                    }}
                    ariaText={"addWorkSpaceSections"}
                    label={"Sections"}
                    selected={false}
                  />
                )}
                {/* Warning icon only when Sections is usable (All Retailers) and impressions are incomplete */}
                {
                  isSectionsLocked !== true && showErrorForSectionButton && (
                    <HtmlTooltip title={
                      <Box width={250}>
                        <StyledMainBox>
                          <Typography variant="body2">
                            Warning: Not all companies in this tenant have an impressions section configured. Automated publishing of impressions reports is disabled for some campaigns.
                          </Typography>
                        </StyledMainBox>
                      </Box>}
                    >
                      <Error
                        sx={{
                          position: "absolute",
                          left: -10,   // adjust to move horizontally
                          bottom: 5,  // adjust to move vertically
                          color: "#AF3241",
                          backgroundColor: "#fff",
                          borderRadius: "50%",
                        }}
                      />
                    </HtmlTooltip>
                  )
                }
              </Box>
            )
          }

        </SpaceBetweenStack>
        <Box height={30} />
        {/* ============ Action button =========== */}
        {checkEditAccess.success === true && (
          <Stack direction="row" spacing={2}>
            <ActionButton
              onClick={(e) => {
                e.preventDefault();
                setNewCampaignModal(true);
              }}
              label={"New Campaign"}
              icon={<Add />}
            />
          </Stack>
        )}

        {/* ============== campaign listing ============ */}
        <Box height={30} />
        {/* -------- filters ------- */}
        {
          typeof Campaigns.campaign_list !== "undefined" && <CampaignListFilters />
        }

        <Box height={30} />
        <CampaignList />
      </StyledMainBox>
      {/* ----- new campaign modal ----- */}
      {newCampaignModal && (
        <AddNewCampaign
          open={newCampaignModal}
          onClose={() => {
            setNewCampaignModal(false);
            resetCampaignSheetData();
          }}
          size={"md"}
          closeModal={() => {
            setNewCampaignModal(false);
            resetCampaignSheetData();
          }}
          usedFor={"add"}
        />
      )}
      {/* ---------- workspace section modal ------------ */}

      {newWorkspaceSectionModal && (
        <WorkspaceSections
          open={newWorkspaceSectionModal}
          size={"xl"}
          closeModal={() => {
            setNewWorkspaceSectionModal(false);
          }}
        />
      )}

    </>
  );
};

export default AccessControl(Campaigns, ["campaign_list_can_view", "campaign_list_can_edit"], [], ["campaigns_section", "campaign_list"]);
